import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
} from '@mui/material';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';

export default function AddFAQModal({ open, onClose, onSuccess, initialValues, qnaId }) {
  const [submitting, setSubmitting] = useState(false);

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      question: '',
      answer: '',
      category: '',
      keywords: '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (open) {
      reset({
        question: initialValues?.question || '',
        answer: initialValues?.answer || '',
        category: '',
        keywords: '',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialValues]);

  const handleClose = () => {
    if (!submitting) {
      reset();
      onClose();
    }
  };

  const onSubmit = async (data) => {
    setSubmitting(true);

    try {
      const createdFAQ = await api.admin.createFAQ({ ...data, qna_id: qnaId });
      toast.success('FAQ created successfully');
      handleClose();
      onSuccess?.(createdFAQ, data);
    } catch (error) {
      console.error('Failed to create FAQ:', error);
      toast.error(error.message || 'Failed to create FAQ');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={submitting}
    >
      <DialogTitle>
        <div className="flex justify-between items-center">
          <span>Add FAQ</span>
          <IconButton onClick={handleClose} disabled={submitting} size="small">
            <X className="w-5 h-5" />
          </IconButton>
        </div>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <div className="space-y-4">
            {/* Question */}
            <Controller
              name="question"
              control={control}
              rules={{ required: 'Question is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Question *"
                  placeholder="Enter the FAQ question"
                  fullWidth
                  multiline
                  rows={2}
                  disabled={submitting}
                  error={!!errors.question}
                  helperText={errors.question?.message}
                />
              )}
            />

            {/* Answer */}
            <Controller
              name="answer"
              control={control}
              rules={{ required: 'Answer is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Answer *"
                  placeholder="Enter the FAQ answer"
                  fullWidth
                  multiline
                  rows={4}
                  disabled={submitting}
                  error={!!errors.answer}
                  helperText={errors.answer?.message}
                />
              )}
            />

            {/* Category */}
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Category (optional)"
                  placeholder="e.g., General, Technical, Billing"
                  fullWidth
                  disabled={submitting}
                />
              )}
            />

            {/* Keywords */}
            <Controller
              name="keywords"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Keywords (optional)"
                  placeholder="Comma-separated keywords for search optimization"
                  fullWidth
                  disabled={submitting}
                  helperText="Example: hours,office,schedule,time"
                />
              )}
            />
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
          >
            {submitting ? 'Creating...' : 'Create FAQ'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
